// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

/*
 * The client portal, mounted on the same PortalLayout chrome as the team-member
 * portal so the restricted surface reads as its own application rather than as
 * a corner of the accountant's one.
 *
 * Every organisation-scoped route here carries
 * `access.client-portal.view-client-portal` — the one permission a portal user
 * gains on accepting their invitation — so the router itself can tell the
 * portal audience from the practice one. The acceptance route alone keeps
 * `permission: '*'`: the acceptor holds no membership yet, which is precisely
 * what that page creates.
 */

/*
 * AMENDMENT, 2026-08-10. The paragraph above no longer describes this file: the
 * borrowed chrome was the defect, not the design. `PortalLayout` heads the page
 * "Team Member Portal" and offers Payslips, which a client's permissions refuse
 * — so the shared shell handed the client an unbranded 403 with no way back,
 * and gave the portal no navigation of its own between its two screens. The
 * tree below now mounts `ClientPortalLayout`, which carries the practice's name
 * and exactly the two destinations a client can open. `PortalLayout` is
 * untouched and still serves the team-member portal.
 *
 * The second paragraph above has since been overtaken as well: the split-06
 * metas replaced `permission: '*'` on the organisation-scoped tree, as its own
 * comment now records.
 */

export default [
    /*
     * The invitation-acceptance page (frontend-15), and the one route in this
     * file that is not organisation-scoped. Its path is not a choice:
     * `CreateClientPortalInvitationProcess` mails
     * `/client-portal/invitations/{token}`, so a rename here dead-links every
     * invitation already sitting in an inbox.
     *
     * `loginRequired: true` because acceptance binds the invitation to a
     * signed-in user — `AcceptClientPortalInvitationProcess` declares
     * `$loginRequired` and the route sits behind `auth:sanctum`. There is no
     * `:organisation` to scope it to: the client holds no membership of the
     * practice until this page succeeds, which is precisely what it creates.
     * `beforeEachRoute` therefore also lists this name in `nonTenantRoutes`.
     */
    {
        path: '/client-portal/invitations/:token',
        component: () => import(/* webpackChunkName: "client-portal" */ '@/pages/client-portal/invitations/[token].vue'),
        name: 'client-portal.accept-invitation',
        meta: {
            guestRequired: false,
            loginRequired: true,
            permission: '*',
        },
    },
    /*
     * The client's landing route. `switchToCompany()` sends a portal client here
     * from the organisation chooser, and it is where an old bookmark of the
     * portal points, so it has to arrive at something: as a bare parent it
     * rendered the shell with no child in it, which under the team-member layout
     * was the "Team Member Portal" heading and a Payslips entry the client
     * cannot open.
     *
     * It redirects to the task list rather than showing a hub of its own. The
     * list IS the portal's home — what the accountant is waiting for — and a hub
     * would put a menu in front of a reader who arrived from an e-mail with one
     * thing on their mind. The shell's navigation carries them to the approvals.
     *
     * The redirect resolves during matching, so `beforeEachRoute` sees
     * `client-portal.tasks` and checks that route's own `meta`.
     */
    {
        path: '/:organisation/portals/clients',
        component: () => import(/* webpackChunkName: "client-portal" */ '@/layouts/ClientPortalLayout.vue'),
        name: 'client-portal.start',
        redirect: { name: 'client-portal.tasks' },
        meta: {
            guestRequired: false,
            loginRequired: true,
            permission: 'access.client-portal.view-client-portal',
        },

        children: [
            {
                path: 'tasks',
                component: () => import(/* webpackChunkName: "client-portal" */ '@/pages/[organisation]/portals/clients/tasks/index.vue'),
                name: 'client-portal.tasks',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.client-portal.view-client-portal',
                },
            },
            /*
             * Finished work, on its own screen (user ruling, 2026-08-13: "Turn
             * Show Completed into a primary action button. That loads the
             * completed tasks on its own page"). It is not offered by the
             * shell — the open list's action bar is the only way in, because
             * the two lists are one section and the sidebar names sections.
             *
             * DECLARED BEFORE `tasks/:id`, and that ordering is load-bearing
             * to a reader even where the matcher does not need it: the literal
             * word "completed" is a legal value for `:id`, so a maintainer
             * moving this below its dynamic sibling has written a route that
             * sends the client's own action to a not-found screen.
             */
            {
                path: 'tasks/completed',
                component: () => import(/* webpackChunkName: "client-portal" */ '@/pages/[organisation]/portals/clients/tasks/completed.vue'),
                name: 'client-portal.completed-tasks',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.client-portal.view-client-portal',
                },
            },
            {
                path: 'tasks/:id',
                component: () => import(/* webpackChunkName: "client-portal" */ '@/pages/[organisation]/portals/clients/tasks/[id].vue'),
                name: 'client-portal.view-task',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.client-portal.view-client-portal',
                },
            },
            {
                path: 'filing-approvals',
                component: () => import(/* webpackChunkName: "client-portal" */ '@/pages/[organisation]/portals/clients/filing-approvals/index.vue'),
                name: 'client-portal.filing-approvals',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.client-portal.view-client-portal',
                },
            },
        ],
    },
];
